import { MONTHS_TR } from './dateUtils';
import { PAYMENT_STATUS } from './constants';
import { formatPhoneDisplay, normalizePhoneTR } from './phoneUtils';
import { normalizeEmail } from './emailUtils';

/**
 * Ödeme dönemi (ay/yıl) matematiği ve liste birleştirme — saf fonksiyonlar.
 * Firestore erişimi yoktur; tüm hesaplar test edilebilir.
 *
 * Tutar kavramları:
 *   dueAmount  → o ay ödenmesi gereken tutar (yalnızca o aya girilen kayıttan; kayıt yoksa 0)
 *   paidAmount → o ay fiilen tahsil edilen tutar
 *   balance    → kalan borç (dueAmount - paidAmount, negatife düşmez)
 */

/**
 * Öğrenci / ödeme kaydındaki tüm olası alanlardan e-posta ve telefonu ayıklar.
 * İkisi de varsa ikisi de döner; tek alanda hangisi varsa o doldurulur.
 */
export const resolveStudentContacts = (student = {}, entry = null) => {
  const candidates = [
    student?.emailOrPhone,
    student?.email,
    student?.phoneNumber,
    student?.phone,
    entry?.studentEmail,
    entry?.studentPhone,
  ]
    .map((value) => String(value ?? '').trim())
    .filter(Boolean);

  let email = '';
  let phoneE164 = '';

  candidates.forEach((value) => {
    if (!email && value.includes('@')) {
      email = normalizeEmail(value) || value;
      return;
    }
    if (!phoneE164) {
      const normalized = normalizePhoneTR(value);
      if (normalized) phoneE164 = normalized;
    }
  });

  const loginId = String(student?.emailOrPhone || entry?.studentEmail || '').trim();

  return {
    email,
    phone: phoneE164 ? formatPhoneDisplay(phoneE164) : '',
    phoneE164,
    emailOrPhone: loginId || email || phoneE164 || '',
  };
};

/** Dönem kimliği: (2026, 9) → "2026-09" */
export const buildPeriodId = (year, month) =>
  `${year}-${String(month).padStart(2, '0')}`;

/** "2026-09" → { year: 2026, month: 9 }, geçersizse null */
export const parsePeriodId = (periodId) => {
  const match = /^(\d{4})-(\d{2})$/.exec(periodId || '');
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return { year, month };
};

/**
 * Firestore belge kimliği — dönem + eğitmen + öğrenci başına tek kayıt.
 * Eğitmen kimliği de kimliğe girer: öğrenci ay içinde eğitmen değiştirdiğinde
 * iki eğitmenin arşivi çakışmaz, her biri kendi kaydını yazabilir.
 */
export const buildPaymentDocId = (year, month, instructorId, studentId) =>
  `${buildPeriodId(year, month)}_${instructorId}_${studentId}`;

/** (2026, 9) → "Eylül 2026" */
export const formatPeriodLabel = (year, month) =>
  `${MONTHS_TR[month - 1] || '—'} ${year}`;

export const getCurrentPeriod = (now = new Date()) => ({
  year: now.getFullYear(),
  month: now.getMonth() + 1,
});

/** Dönemi ay bazında kaydırır: delta negatif = geçmişe doğru */
export const shiftPeriod = ({ year, month }, delta) => {
  const absolute = year * 12 + (month - 1) + delta;
  const normalizedMonth = ((absolute % 12) + 12) % 12;
  return {
    year: Math.floor(absolute / 12),
    month: normalizedMonth + 1,
  };
};

/** Kronolojik karşılaştırma: negatif ise a daha eski */
export const comparePeriods = (a, b) =>
  (a.year - b.year) || (a.month - b.month);

export const isPastPeriod = (year, month, now = new Date()) =>
  comparePeriods({ year, month }, getCurrentPeriod(now)) < 0;

export const isFuturePeriod = (year, month, now = new Date()) =>
  comparePeriods({ year, month }, getCurrentPeriod(now)) > 0;

/** Ödeme takibi ve arşiv 2026'dan başlar */
export const MIN_PAYMENT_YEAR = 2026;

/** Peşin ödeme / önceden ücret girişi için içinde bulunulan yıldan sonra açık yıl sayısı */
export const PAYMENT_YEARS_AHEAD = 5;

export const getMaxPaymentYear = (now = new Date()) =>
  Math.max(MIN_PAYMENT_YEAR, getCurrentPeriod(now).year) + PAYMENT_YEARS_AHEAD;

export const clampPaymentYear = (year, now = new Date()) => {
  const n = Number(year);
  if (!Number.isInteger(n)) return getCurrentPeriod(now).year;
  return Math.min(getMaxPaymentYear(now), Math.max(MIN_PAYMENT_YEAR, n));
};

/** 2026'dan açık olan son yıla kadar, yeniden eskiye */
export const buildYearOptions = (_earliestYear, now = new Date()) => {
  const years = [];
  for (let year = getMaxPaymentYear(now); year >= MIN_PAYMENT_YEAR; year--) years.push(year);
  return years;
};

const normalizeStatus = (status) =>
  status === PAYMENT_STATUS.PAID ? PAYMENT_STATUS.PAID : PAYMENT_STATUS.UNPAID;

const normalizeAmount = (amount) => {
  const value = Number(amount);
  return Number.isFinite(value) && value > 0 ? value : 0;
};

/** Öğrencinin tanımlı aylık ücreti (aidat) */
export const resolveMonthlyFee = (student) => normalizeAmount(student?.monthlyFee);

/** Ücret geçmişindeki ilk kaydın başlangıcı: ilk girilen ücret tüm aylara uygulanır */
export const FEE_HISTORY_EPOCH = '2000-01';
export const MAX_FEE_HISTORY = 120;

const validFeeHistory = (student) => (
  Array.isArray(student?.monthlyFeeHistory)
    ? student.monthlyFeeHistory
      .filter((item) => parsePeriodId(item?.from) && Number.isFinite(Number(item?.amount)))
      .sort((a, b) => a.from.localeCompare(b.from))
    : []
);

/**
 * O dönemde geçerli aylık ücret. Ücret değiştiğinde geçmiş aylar eski ücretle kalır:
 * monthlyFeeHistory = [{ from: 'YYYY-MM', amount }] — dönemden önceki son kayıt geçerlidir.
 * Geçmiş yoksa (eski kayıtlar) güncel monthlyFee kullanılır.
 */
export const resolveFeeForPeriod = (student, year, month) => {
  const history = validFeeHistory(student);
  if (history.length === 0 || !year || !month) return resolveMonthlyFee(student);
  const period = buildPeriodId(year, month);
  let fee = null;
  history.forEach((item) => {
    if (item.from <= period) fee = item.amount;
  });
  return normalizeAmount(fee ?? history[0].amount);
};

/**
 * Yeni aylık ücret için ücret geçmişi: değişiklik içinde bulunulan aydan başlar.
 * İlk kez ücret giriliyorsa tüm aylara uygulanır; aynı ay içindeki düzeltme o ayın kaydını ezer.
 */
export const buildNextFeeHistory = (student, nextFee, now = new Date()) => {
  const amount = normalizeAmount(nextFee);
  const current = buildPeriodId(getCurrentPeriod(now).year, getCurrentPeriod(now).month);
  let history = validFeeHistory(student).map(({ from, amount: value }) => ({ from, amount: normalizeAmount(value) }));
  const previousFee = resolveMonthlyFee(student);

  if (history.length === 0) {
    if (previousFee <= 0) return [{ from: FEE_HISTORY_EPOCH, amount }];
    history = [{ from: FEE_HISTORY_EPOCH, amount: previousFee }];
  }
  if (history[history.length - 1].amount === amount) return history;

  history = history.filter((item) => item.from < current);
  if (history.length === 0) return [{ from: FEE_HISTORY_EPOCH, amount }];
  return [...history, { from: current, amount }].slice(-MAX_FEE_HISTORY);
};

/**
 * O ay ödenmesi gereken tutar yalnızca o aya girilmiş kayıttan gelir. Kaydı olmayan ay 0'dır:
 * aylık ücret diğer aylara ve sonraki yıllara kendiliğinden yansımaz.
 * Tutar alanı olmayan eski kayıtlar o dönemin aylık ücretiyle gösterilir.
 */
const resolveDueAmount = (entry, student, period) => {
  if (!entry) return 0;
  return entry.dueAmount != null
    ? normalizeAmount(entry.dueAmount)
    : resolveFeeForPeriod(student, period?.year, period?.month);
};

/** Tahsil edilen tutar (eski kayıtlarda alan adı "amount" idi) */
const resolvePaidAmount = (entry) =>
  normalizeAmount(entry?.paidAmount != null ? entry.paidAmount : entry?.amount);

const amountsFor = (entry, student, period = null) => {
  const dueAmount = resolveDueAmount(entry, student, period);
  const paidAmount = resolvePaidAmount(entry);
  return {
    dueAmount,
    paidAmount,
    balance: Math.max(0, dueAmount - paidAmount),
  };
};

const rowFromEntry = (student, entry) => ({
  studentId: student?.id || entry?.studentId,
  studentName: student?.name || entry?.studentName || 'Kaydı silinmiş öğrenci',
  emailOrPhone: student?.emailOrPhone || entry?.studentEmail || '',
  monthlyFee: resolveMonthlyFee(student),
  status: normalizeStatus(entry?.status),
  ...amountsFor(entry, student, parsePeriodId(entry?.period)),
  note: entry?.note || '',
  paidAt: entry?.paidAt || null,
  updatedAt: entry?.updatedAt || null,
  entryId: entry?.id || null,
  hasRecord: Boolean(entry),
  /** Öğrenci artık eğitmene bağlı değil ama arşiv kaydı korunuyor */
  isOrphan: !student,
});

/**
 * Öğrenci listesi + Firestore kayıtlarını tek satır listesine indirger.
 * Kayıt yoksa "Ödemedi" kabul edilir (boş dönem için yazma maliyeti yok).
 * Eğitmenden ayrılmış öğrencilerin arşiv kayıtları da listeye eklenir.
 */
export const buildPaymentRows = (students, entries) => {
  const entryByStudent = new Map();
  (entries || []).forEach((entry) => {
    if (entry?.studentId) entryByStudent.set(entry.studentId, entry);
  });

  const studentList = students || [];
  const rows = studentList.map((student) =>
    rowFromEntry(student, entryByStudent.get(student.id) || null)
  );

  const knownIds = new Set(studentList.map((s) => s.id));
  (entries || []).forEach((entry) => {
    if (entry?.studentId && !knownIds.has(entry.studentId)) {
      rows.push(rowFromEntry(null, entry));
    }
  });

  return rows.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || '', 'tr'));
};

export const summarizePayments = (rows) => {
  const list = rows || [];
  const total = list.length;
  const paid = list.filter((r) => r.status === PAYMENT_STATUS.PAID).length;
  return {
    total,
    paid,
    unpaid: total - paid,
    due: list.reduce((sum, r) => sum + r.dueAmount, 0),
    collected: list.reduce((sum, r) => sum + r.paidAmount, 0),
    outstanding: list.reduce((sum, r) => sum + r.balance, 0),
    rate: total > 0 ? Math.round((paid / total) * 100) : 0,
  };
};

/**
 * Yıllık tablo hücre durumu.
 * PENDING: henüz gelmemiş ay — tabloda "ödemedi" kırmızısı basılmaz.
 * Tutarlar yalnızca kaydı olan aylardan gelir.
 */
export const PAYMENT_CELL = {
  PAID: 'paid',
  UNPAID: 'unpaid',
  PENDING: 'pending',
};

export const MONTH_NUMBERS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const cellFromEntry = (year, month, entry, student, now) => {
  const hasRecord = Boolean(entry);
  const status = normalizeStatus(entry?.status);
  const future = isFuturePeriod(year, month, now);

  let state;
  if (hasRecord) {
    state = status === PAYMENT_STATUS.PAID ? PAYMENT_CELL.PAID : PAYMENT_CELL.UNPAID;
  } else {
    state = future ? PAYMENT_CELL.PENDING : PAYMENT_CELL.UNPAID;
  }

  return {
    month,
    year,
    state,
    status,
    isFuture: future,
    hasRecord,
    entryId: entry?.id || null,
    ...amountsFor(entry, student, { year, month }),
    note: entry?.note || '',
    updatedAt: entry?.updatedAt || null,
  };
};

/**
 * Excel benzeri yıllık tablo: her satır bir öğrenci, her hücre bir ay.
 * Eğitmenden ayrılmış öğrencilerin arşiv kayıtları da satır olarak korunur.
 */
/** Kaydı olan aylarda en sık girilen "ödenmesi gereken" tutar (eşitlikte büyük olan) */
export const mostCommonDueAmount = (cells) => {
  const counts = new Map();
  (cells || []).forEach((cell) => {
    if (cell?.hasRecord && cell.dueAmount > 0) {
      counts.set(cell.dueAmount, (counts.get(cell.dueAmount) || 0) + 1);
    }
  });
  let best = 0;
  let bestCount = 0;
  counts.forEach((count, amount) => {
    if (count > bestCount || (count === bestCount && amount > best)) {
      best = amount;
      bestCount = count;
    }
  });
  return best;
};

/**
 * includeOrphans=false: yalnızca verilen öğrenciler. Ayrılan / silinen öğrencilerin
 * kayıtları silinmez; ödeme listesinde değil Arşiv'de gösterilir.
 */
export const buildYearMatrix = (students, entries, {
  year, now = new Date(), includeOrphans = true,
} = {}) => {
  const entryByStudentMonth = new Map();
  (entries || []).forEach((entry) => {
    const parsed = parsePeriodId(entry?.period) || (
      Number.isInteger(entry?.year) && Number.isInteger(entry?.month)
        ? { year: entry.year, month: entry.month }
        : null
    );
    if (!entry?.studentId || !parsed || parsed.year !== year) return;
    entryByStudentMonth.set(`${entry.studentId}:${parsed.month}`, entry);
  });

  const studentList = students || [];
  const knownIds = new Set(studentList.map((s) => s.id));
  const orphanIds = [];
  const orphanSamples = new Map();

  (entries || []).forEach((entry) => {
    if (!includeOrphans || !entry?.studentId || knownIds.has(entry.studentId)) return;
    if (!orphanSamples.has(entry.studentId)) {
      orphanIds.push(entry.studentId);
      orphanSamples.set(entry.studentId, entry);
    }
  });

  const descriptors = [
    ...studentList.map((student) => ({ student, isOrphan: false })),
    ...orphanIds.map((studentId) => {
      const sample = orphanSamples.get(studentId);
      return {
        student: {
          id: studentId,
          name: sample?.studentName || 'Kaydı silinmiş öğrenci',
          emailOrPhone: sample?.studentEmail || '',
          phoneNumber: sample?.studentPhone || '',
        },
        isOrphan: true,
      };
    }),
  ];

  return descriptors
    .map(({ student, isOrphan }) => {
      const cells = MONTH_NUMBERS.map((month) => cellFromEntry(
        year,
        month,
        entryByStudentMonth.get(`${student.id}:${month}`) || null,
        isOrphan ? null : student,
        now
      ));
      const entrySample = MONTH_NUMBERS
        .map((month) => entryByStudentMonth.get(`${student.id}:${month}`))
        .find(Boolean) || null;
      const contacts = resolveStudentContacts(student, entrySample);
      const monthlyFee = resolveMonthlyFee(isOrphan ? null : student);

      return {
        studentId: student.id,
        studentName: student.name || 'İsimsiz öğrenci',
        userCode: student.userCode || '',
        emailOrPhone: contacts.emailOrPhone,
        email: contacts.email,
        phone: contacts.phone,
        phoneNumber: contacts.phoneE164 || student.phoneNumber || '',
        monthlyFee,
        /** Ücret alanı boşsa aylara girilen tutar esas alınır: uyarı yalnızca hiç tutar yokken çıkar */
        effectiveMonthlyFee: monthlyFee || mostCommonDueAmount(cells),
        isOrphan,
        cells,
        paidCount: cells.filter((c) => c.state === PAYMENT_CELL.PAID).length,
        unpaidCount: cells.filter((c) => c.state === PAYMENT_CELL.UNPAID).length,
        /** Yıllık beklenen: kaydı olan ayların tutarları toplamı */
        due: cells.reduce((sum, c) => sum + c.dueAmount, 0),
        /** Peşin ödemeler dahil toplam tahsilat */
        collected: cells.reduce((sum, c) => sum + c.paidAmount, 0),
        outstanding: cells.reduce((sum, c) => sum + c.balance, 0),
      };
    })
    .sort((a, b) => (a.studentName || '').localeCompare(b.studentName || '', 'tr'));
};

/** Tablo altı ay toplamları */
export const buildMonthTotals = (rows) =>
  MONTH_NUMBERS.map((month, index) => {
    const cells = (rows || []).map((row) => row.cells[index]).filter(Boolean);
    return {
      month,
      paid: cells.filter((c) => c.state === PAYMENT_CELL.PAID).length,
      unpaid: cells.filter((c) => c.state === PAYMENT_CELL.UNPAID).length,
      pending: cells.filter((c) => c.state === PAYMENT_CELL.PENDING).length,
      due: cells.reduce((sum, c) => sum + c.dueAmount, 0),
      collected: cells.reduce((sum, c) => sum + c.paidAmount, 0),
      outstanding: cells.reduce((sum, c) => sum + c.balance, 0),
    };
  });

/**
 * Yıl özeti — tüm öğrencilerin toplamı.
 *
 * Ay birimi "öğrenci × ay" hücresidir: 200 öğrenci × 12 ay = 2400 ay.
 * Ay sayıları 12 ay üzerindendir: vadesi henüz gelmemiş aylar da ödenmemiş
 * sayılır, peşin ödenen gelecek aylar da ödenmiş sayılır.
 */
export const summarizeYearMatrix = (rows) => {
  const list = rows || [];
  const totalMonths = list.reduce((sum, row) => sum + row.cells.length, 0);
  const paid = list.reduce((sum, row) => sum + row.paidCount, 0);
  const due = list.reduce((sum, row) => sum + row.due, 0);
  const collected = list.reduce((sum, row) => sum + row.collected, 0);
  return {
    studentCount: list.length,
    /** Takip edilen toplam ay hücresi: öğrenci sayısı × 12 */
    totalMonths,
    /** Ödenen ay — peşin ödenmiş gelecek aylar dahil */
    paid,
    /** Ödenmemiş ay — vadesi gelmemiş aylar dahil, 12 ay üzerinden */
    unpaid: totalMonths - paid,
    /** Vadesi geldiği hâlde ödenmemiş ay — tablodaki kırmızı hücreler */
    overdue: list.reduce((sum, row) => sum + row.unpaidCount, 0),
    due,
    collected,
    outstanding: list.reduce((sum, row) => sum + row.outstanding, 0),
    /** 12 ay üzerinden tamamlanma oranı */
    rate: totalMonths > 0 ? Math.round((paid / totalMonths) * 100) : 0,
    /** Para bazlı tahsilat oranı */
    collectionRate: due > 0 ? Math.round((collected / due) * 100) : 0,
    /** Aylık ücreti tanımlanmamış öğrenciler — takip edilemez durum */
    missingFeeCount: list.filter(
      (row) => !row.isOrphan && !((row.effectiveMonthlyFee ?? row.monthlyFee) > 0)
    ).length,
  };
};

/**
 * Arşivdeki "Ayrılan öğrenciler": artık eğitmenin listesinde olmayan öğrencilerin
 * kayıtlarını öğrenci bazında özetler. Aynı kayıt iki kaynaktan gelirse bir kez sayılır.
 */
export const groupDepartedStudents = (entries, currentStudentIds = []) => {
  const current = new Set(currentStudentIds);
  const seen = new Set();
  const byStudent = new Map();

  (entries || []).forEach((entry) => {
    if (!entry?.studentId || current.has(entry.studentId) || seen.has(entry.id)) return;
    seen.add(entry.id);
    const periodId = parsePeriodId(entry.period) ? entry.period : '';
    const { dueAmount, paidAmount, balance } = amountsFor(entry, null);
    const group = byStudent.get(entry.studentId) || {
      studentId: entry.studentId,
      studentName: entry.studentName || 'Kaydı silinmiş öğrenci',
      studentEmail: entry.studentEmail || '',
      recordCount: 0,
      firstPeriod: periodId,
      lastPeriod: periodId,
      due: 0,
      collected: 0,
      outstanding: 0,
      reason: entry.archivedReason || '',
      archivedAt: entry.archivedAt || null,
    };
    group.recordCount += 1;
    group.due += dueAmount;
    group.collected += paidAmount;
    group.outstanding += balance;
    if (periodId && (!group.firstPeriod || periodId < group.firstPeriod)) group.firstPeriod = periodId;
    if (periodId && periodId > group.lastPeriod) group.lastPeriod = periodId;
    if (!group.reason && entry.archivedReason) group.reason = entry.archivedReason;
    byStudent.set(entry.studentId, group);
  });

  return Array.from(byStudent.values())
    .sort((a, b) => a.studentName.localeCompare(b.studentName, 'tr'));
};

/**
 * Arşiv görünümü: kayıtları döneme göre gruplar, yeniden eskiye sıralar.
 * Sayımlar yalnızca kaydı olan öğrencileri kapsar (o dönemde ne yazıldıysa).
 */
export const groupEntriesByPeriod = (entries) => {
  const byPeriod = new Map();

  (entries || []).forEach((entry) => {
    const parsed = parsePeriodId(entry?.period) || (
      Number.isInteger(entry?.year) && Number.isInteger(entry?.month)
        ? { year: entry.year, month: entry.month }
        : null
    );
    if (!parsed) return;

    const periodId = buildPeriodId(parsed.year, parsed.month);
    if (!byPeriod.has(periodId)) {
      byPeriod.set(periodId, {
        periodId,
        year: parsed.year,
        month: parsed.month,
        label: formatPeriodLabel(parsed.year, parsed.month),
        entries: [],
      });
    }
    byPeriod.get(periodId).entries.push(entry);
  });

  return Array.from(byPeriod.values())
    .map((group) => {
      const rows = group.entries.map((entry) => rowFromEntry(null, entry));
      const {
        total, paid, unpaid, collected, outstanding, rate,
      } = summarizePayments(rows);
      return { ...group, total, paid, unpaid, collected, outstanding, rate };
    })
    .sort((a, b) => comparePeriods(b, a));
};
